// ENG-6022: pure resolver for the channel listener launch flags the manager
// bakes into a persistent claude-code session at spawn time.
//
// Why this exists: the Teams channel shipped end-to-end (creds written,
// `.mcp.json` gained the `msteams` key, the MCP booted and ingested inbound)
// but replies never came — the devChannels construction inside processAgent
// was a closed hand-maintained list with cases for slack/telegram/discord
// only, so the session was never launched with `server:msteams` and the
// channel pipeline had no subscriber. Same closed-list failure mode as
// ENG-6019 (build:mcp-assets / deployMcpAssets). Pulled out as a pure
// function so the gating logic is unit-testable without mocking
// processAgent's dozens of dependencies (the channel-restart-decision.ts
// pattern), and so adding a bundled channel MCP is a one-line edit to
// DEV_CHANNEL_SERVER_IDS with test coverage that catches the omission.

export interface ChannelConfigEntry {
  config: unknown;
  status: string;
}

export interface ChannelLaunchFlags {
  /**
   * `server:<id>` entries for `--dangerously-load-development-channels` —
   * our custom per-agent channel MCP servers (written into the provision
   * .mcp.json by writeChannelCredentials). The `server:<name>` form
   * registers them with Claude Code's channel notification pipeline.
   * Telegram used to ride the `plugin:telegram@claude-plugins-official`
   * plugin, which fails with >1 agent per host because the plugin reads a
   * single global bot-token .env — see ENG-4437.
   */
  devChannels: string[];
  /** `plugin:<name>@<marketplace>` entries for official plugin channels. */
  pluginChannels: string[];
}

/**
 * Channel ids whose MCP servers ship in the CLI bundle (`build:mcp-assets`
 * → `_mcp/<id>-channel.js`, with msteams mapping to teams-channel.js) and
 * register via the `server:<id>` dev-channel mechanism. direct-chat is NOT
 * here — it's unconditional for persistent sessions and appended by the
 * caller regardless of channel_configs.
 */
const DEV_CHANNEL_SERVER_IDS = ['slack', 'telegram', 'msteams', 'whatsapp'] as const;

/**
 * Resolve the channel launch flags for one agent from its channel configs.
 *
 * Gate registration on the channel actually being provisioned — a config
 * row with status other than `active` / `pending` never triggers
 * writeChannelCredentials, so asking Claude Code to load a dev channel
 * with no MCP entry would fail with a confusing "unknown channel" error.
 * A row with null config never triggers writeChannelCredentials upstream
 * either, so it gets the same gate.
 *
 * ENG-5932: a locally-quarantined channel has had its `.mcp.json` entry
 * removed, so registering `server:<id>` in the launch flags would point
 * Claude at an absent MCP entry — the exact "unknown channel" failure mode
 * this gate already guards against for unprovisioned rows. Exclude
 * quarantined channels here too, so a respawn (which rebuilds devChannels
 * from raw channel_configs) can't silently re-add a quarantined channel
 * and revive the flap. Empty set in shadow/off mode.
 */
export function resolveChannelLaunchFlags(
  channelConfigs: Record<string, ChannelConfigEntry | undefined> | null | undefined,
  quarantinedChannels: ReadonlySet<string>,
): ChannelLaunchFlags {
  const devChannels: string[] = [];
  const pluginChannels: string[] = [];
  if (!channelConfigs) return { devChannels, pluginChannels };

  const isChannelEnabled = (id: string): boolean => {
    if (quarantinedChannels.has(id)) return false;
    const entry = channelConfigs[id];
    return !!entry?.config && (entry.status === 'active' || entry.status === 'pending');
  };

  for (const id of DEV_CHANNEL_SERVER_IDS) {
    if (isChannelEnabled(id)) devChannels.push(`server:${id}`);
  }
  if (isChannelEnabled('discord')) {
    pluginChannels.push('plugin:discord@claude-plugins-official');
  }
  return { devChannels, pluginChannels };
}
